
import { api, type ApiError } from './client';
import type { CEFRLevel } from './courses';
import { useAuthStore } from '@/stores/auth';

export type DiagnosticExam = {
  id: string;
  student_id: string;
  target_level: CEFRLevel;
  step: number;
  status: 'in_progress' | 'completed' | 'expired';
  score?: number;
  result_level?: CEFRLevel;
  started_at: string;
  expires_at?: string;
  completed_at?: string;
};

export type SkillRadar = {
  reading: { score: number; level: CEFRLevel };
  listening: { score: number; level: CEFRLevel };
  speaking: { score: number; level: CEFRLevel };
  writing: { score: number; level: CEFRLevel };
  grammar: { score: number; level: CEFRLevel };
  vocabulary: { score: number; level: CEFRLevel };
};

export type LevelProgress = {
  current_level: CEFRLevel;
  next_level: CEFRLevel;
  percent_to_next: number;
  lessons_to_next: number;
};

// Helper: the current student's id. The backend resolves the student from a
// `student_id` query/body param rather than the bearer token, so every
// student-scoped call must pass it explicitly.
function currentStudentId(): string {
  return useAuthStore.getState().user?.id ?? '';
}

// The backend DiagnosticExam model serialises the target level under the
// `level` json tag (not `target_level`). Adapt it to the frontend contract.
type BackendExam = {
  id: string;
  student_id: string;
  level: CEFRLevel;
  step: number;
  status: DiagnosticExam['status'];
  score?: number;
  result_level?: CEFRLevel;
  started_at: string;
  expires_at?: string;
  completed_at?: string;
};

function adaptExam(e: BackendExam): DiagnosticExam {
  return {
    id: e.id,
    student_id: e.student_id,
    target_level: e.level,
    step: e.step,
    status: e.status,
    score: e.score,
    result_level: e.result_level,
    started_at: e.started_at,
    expires_at: e.expires_at,
    completed_at: e.completed_at,
  };
}

export const cefrApi = {
  async startExam(targetLevel: CEFRLevel) {
    const { data } = await api.post('/cefr/student/exam/start', {
      student_id: currentStudentId(),
      target_level: targetLevel,
    });
    return adaptExam(data as BackendExam);
  },
  async getActiveExam(): Promise<DiagnosticExam | null> {
    // The backend replies 404 ("no active exam") when the student has never
    // started one — that is the common first-load state, not an error. Treat
    // it as an empty result so the page can render the "start exam" form
    // instead of getting stuck behind a thrown query error.
    try {
      const { data } = await api.get('/cefr/student/exam/active', {
        params: { student_id: currentStudentId() },
      });
      if (!data) return null;
      return adaptExam(data as BackendExam);
    } catch (err) {
      // Перехватчик client.ts отклоняет ApiError, а не ошибку axios (техдолг 292).
      if ((err as ApiError | undefined)?.status === 404) {
        return null;
      }
      throw err;
    }
  },
  async getExam(id: string) {
    const { data } = await api.get(`/cefr/student/exam/${id}`);
    return adaptExam(data as BackendExam);
  },
  // CEFR-001 fix: server now computes the score from `answers`. We no longer
  // ship a `score` field — the backend ignores it even if sent.
  async submitStep(payload: { exam_id: string; answers: Record<string, unknown> }) {
    const { data } = await api.post('/cefr/student/exam/submit', payload);
    return adaptExam(data as BackendExam);
  },
  async getSkills() {
    // Backend returns a flat array [{ skill, level, score }] (aliased to
    // `.items` by client.ts). Reshape into the keyed SkillRadar object.
    const { data } = await api.get('/cefr/student/skills', {
      params: { student_id: currentStudentId() },
    });
    const items = ((data?.items ?? data) as Array<{
      skill: string;
      level: CEFRLevel;
      score: number;
    }>) ?? [];

    const empty = (): { score: number; level: CEFRLevel } => ({ score: 0, level: 'A1' });
    const radar: SkillRadar = {
      reading: empty(),
      listening: empty(),
      speaking: empty(),
      writing: empty(),
      grammar: empty(),
      vocabulary: empty(),
    };
    for (const s of items) {
      if (s.skill in radar) {
        (radar as Record<string, { score: number; level: CEFRLevel }>)[s.skill] = {
          score: s.score,
          level: s.level,
        };
      }
    }
    return radar;
  },
  async getProgress() {
    // Backend returns { current_level, next_level, total_points,
    // points_to_next }. The frontend contract wants percent_to_next and
    // lessons_to_next, so derive them from the points figures.
    const { data } = await api.get('/cefr/student/progress', {
      params: { student_id: currentStudentId() },
    });
    const raw = data as {
      current_level: CEFRLevel;
      next_level: CEFRLevel;
      total_points: number;
      points_to_next: number;
    };
    const total = raw.total_points ?? 0;
    const remaining = raw.points_to_next ?? 0;
    const target = total + Math.max(remaining, 0);
    const percent = target > 0 ? Math.min(100, Math.round((total / target) * 100)) : 0;
    return {
      current_level: raw.current_level,
      next_level: raw.next_level,
      percent_to_next: percent,
      lessons_to_next: Math.max(remaining, 0),
    } as LevelProgress;
  },
  async getLevel() {
    const { data } = await api.get('/cefr/student/level', {
      params: { student_id: currentStudentId() },
    });
    return data as { level: CEFRLevel };
  },
};
